import { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { api, AdminAnnouncement } from '../api/client';
import { H2, Muted } from './ui';

function blank(): AdminAnnouncement {
  return {
    id: 'aviso-' + Date.now().toString(36),
    active: true,
    type: 'modal',
    title: '',
    body: '',
    image: null,
    link: null,
    start: null,
    end: null,
    frequency: 'once',
    everyDays: 3,
    closeDelay: 0,
  };
}

// Panel de administración de avisos (dentro de /uso). CRUD sobre el JSON del server.
export function AnnouncementsAdmin({ adminKey }: { adminKey: string }) {
  const [items, setItems] = useState<AdminAnnouncement[] | null>(null);
  const [editIdx, setEditIdx] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const fileRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    api
      .announcementsAll(adminKey)
      .then((d) => setItems(d.announcements))
      .catch((e) => setMsg(e instanceof Error ? e.message : 'Error al cargar'));
  }, [adminKey]);

  if (items === null) {
    return (
      <>
        <H2>Avisos</H2>
        <Muted>{msg || 'Cargando…'}</Muted>
      </>
    );
  }

  const patch = (i: number, p: Partial<AdminAnnouncement>) =>
    setItems((list) => (list ? list.map((it, k) => (k === i ? { ...it, ...p } : it)) : list));

  const setLink = (i: number, url: string, label: string) =>
    patch(i, { link: url.trim() ? { url: url.trim(), label: label.trim() || 'Ver más' } : null });

  const addNew = () => {
    setItems((list) => [...(list ?? []), blank()]);
    setEditIdx(items.length);
    setMsg('');
  };

  const del = (i: number) => {
    setItems((list) => (list ? list.filter((_, k) => k !== i) : list));
    setEditIdx(null);
  };

  const save = async () => {
    setBusy(true);
    setMsg('');
    try {
      const r = await api.announcementsSave(adminKey, items);
      setMsg(`Guardado (${r.count} aviso${r.count === 1 ? '' : 's'}).`);
      setEditIdx(null);
    } catch (e) {
      setMsg(e instanceof Error ? e.message : 'Error al guardar');
    } finally {
      setBusy(false);
    }
  };

  const upload = async (i: number, file: File) => {
    setBusy(true);
    setMsg('');
    try {
      const r = await api.uploadAnnouncementImage(adminKey, file);
      patch(i, { image: r.url });
    } catch (e) {
      setMsg(e instanceof Error ? e.message : 'Error al subir imagen');
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <H2>Avisos</H2>
      {items.length === 0 && <Muted>No hay avisos. Creá uno con “+ Nuevo aviso”.</Muted>}

      {items.map((a, i) => (
        <ItemCard key={i} $off={!a.active}>
          <button className="head" onClick={() => setEditIdx(editIdx === i ? null : i)}>
            <span className="ttl">{a.title || a.id}</span>
            <span className="meta">
              {a.active ? '' : 'inactivo · '}
              {a.frequency}
              {editIdx === i ? ' ▲' : ' ▼'}
            </span>
          </button>

          {editIdx === i && (
            <Form>
              <label>
                <span>Activo</span>
                <input type="checkbox" checked={a.active} onChange={(e) => patch(i, { active: e.target.checked })} />
              </label>
              <label>
                <span>ID (único)</span>
                <input value={a.id} onChange={(e) => patch(i, { id: e.target.value })} placeholder="promo-julio" />
              </label>
              <label>
                <span>Título</span>
                <input value={a.title} onChange={(e) => patch(i, { title: e.target.value })} maxLength={120} />
              </label>
              <label>
                <span>Texto</span>
                <textarea value={a.body} onChange={(e) => patch(i, { body: e.target.value })} rows={3} maxLength={600} />
              </label>

              <label>
                <span>Imagen (URL o subir)</span>
                <input
                  value={a.image ?? ''}
                  onChange={(e) => patch(i, { image: e.target.value || null })}
                  placeholder="https://… o subí un archivo"
                />
              </label>
              <Inline>
                <button type="button" className="ghost" onClick={() => fileRef.current?.click()} disabled={busy}>
                  📷 Subir imagen
                </button>
                {a.image && (
                  <button type="button" className="ghost" onClick={() => patch(i, { image: null })}>
                    Quitar
                  </button>
                )}
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/*"
                  style={{ display: 'none' }}
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) upload(i, f);
                    e.target.value = '';
                  }}
                />
              </Inline>
              {a.image && <img className="preview" src={a.image} alt="" />}

              <label>
                <span>Link — URL (opcional)</span>
                <input
                  value={a.link?.url ?? ''}
                  onChange={(e) => setLink(i, e.target.value, a.link?.label ?? '')}
                  placeholder="https://tienda…"
                />
              </label>
              <label>
                <span>Link — texto del botón</span>
                <input
                  value={a.link?.label ?? ''}
                  onChange={(e) => setLink(i, a.link?.url ?? '', e.target.value)}
                  placeholder="Ver oferta"
                />
              </label>

              <Two>
                <label>
                  <span>Desde</span>
                  <input type="date" value={a.start ?? ''} onChange={(e) => patch(i, { start: e.target.value || null })} />
                </label>
                <label>
                  <span>Hasta</span>
                  <input type="date" value={a.end ?? ''} onChange={(e) => patch(i, { end: e.target.value || null })} />
                </label>
              </Two>

              <label>
                <span>Frecuencia</span>
                <select
                  value={a.frequency}
                  onChange={(e) => patch(i, { frequency: e.target.value as AdminAnnouncement['frequency'] })}
                >
                  <option value="once">Una vez por dispositivo</option>
                  <option value="always">En cada apertura</option>
                  <option value="everyNDays">Cada N días</option>
                </select>
              </label>
              {a.frequency === 'everyNDays' && (
                <label>
                  <span>Cada cuántos días</span>
                  <input
                    type="number"
                    min={1}
                    max={365}
                    value={a.everyDays}
                    onChange={(e) => patch(i, { everyDays: Number(e.target.value) })}
                  />
                </label>
              )}
              <label>
                <span>Cuenta regresiva para cerrar (seg, 0 = cerrar ya)</span>
                <input
                  type="number"
                  min={0}
                  max={60}
                  value={a.closeDelay}
                  onChange={(e) => patch(i, { closeDelay: Number(e.target.value) })}
                />
              </label>

              <button type="button" className="danger" onClick={() => del(i)}>
                Borrar este aviso
              </button>
            </Form>
          )}
        </ItemCard>
      ))}

      <Actions>
        <button type="button" className="ghost" onClick={addNew} disabled={busy}>
          + Nuevo aviso
        </button>
        <button type="button" className="primary" onClick={save} disabled={busy}>
          {busy ? 'Guardando…' : 'Guardar cambios'}
        </button>
      </Actions>
      {msg && <Muted role="status" style={{ textAlign: 'center' }}>{msg}</Muted>}
    </>
  );
}

const ItemCard = styled.div<{ $off: boolean }>`
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 10px;
  margin-bottom: 8px;
  opacity: ${({ $off }) => ($off ? 0.6 : 1)};
  overflow: hidden;
  .head {
    width: 100%;
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 8px;
    background: ${({ theme }) => theme.colors.surface};
    border: none;
    color: ${({ theme }) => theme.colors.text};
    padding: 12px 14px;
    text-align: left;
  }
  .ttl { font-weight: 700; }
  .meta { font-size: 0.7rem; color: ${({ theme }) => theme.colors.textMuted}; white-space: nowrap; }
`;

const Form = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 12px 14px 16px;
  background: ${({ theme }) => theme.colors.surfaceAlt};

  label {
    display: flex;
    flex-direction: column;
    gap: 4px;
    font-size: 0.75rem;
    color: ${({ theme }) => theme.colors.textMuted};
  }
  label > span { font-weight: 600; }
  input, textarea, select {
    background: ${({ theme }) => theme.colors.bg};
    color: ${({ theme }) => theme.colors.text};
    border: 1px solid ${({ theme }) => theme.colors.borderStrong};
    border-radius: 8px;
    padding: 9px 10px;
    font: inherit;
    font-size: 0.88rem;
  }
  input[type='checkbox'] { width: 20px; height: 20px; }
  .preview { width: 100%; border-radius: 8px; }
  .danger {
    background: none;
    border: 1px solid ${({ theme }) => theme.colors.live};
    color: ${({ theme }) => theme.colors.live};
    border-radius: 8px;
    padding: 9px;
    font-weight: 600;
    margin-top: 4px;
  }
`;

const Inline = styled.div`
  display: flex;
  gap: 8px;
  .ghost {
    background: none;
    border: 1px solid ${({ theme }) => theme.colors.borderStrong};
    color: ${({ theme }) => theme.colors.text};
    border-radius: 8px;
    padding: 8px 12px;
    font-weight: 600;
    font-size: 0.81rem;
  }
`;

const Two = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
`;

const Actions = styled.div`
  display: flex;
  gap: 8px;
  margin: 12px 0 6px;
  button { flex: 1; border-radius: 10px; padding: 12px; font-weight: 700; }
  .ghost {
    background: none;
    border: 1px solid ${({ theme }) => theme.colors.borderStrong};
    color: ${({ theme }) => theme.colors.text};
  }
  .primary {
    background: ${({ theme }) => theme.colors.accent};
    color: ${({ theme }) => theme.colors.accentText};
    border: none;
  }
`;
